import React, { useState, useEffect } from 'react';
import { CreditCardIcon, BanknotesIcon, WalletIcon } from '@heroicons/react/24/outline';
import { walletService } from '../../services/wallet.service';

interface PaymentMethod {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  processingTime: string;
  recommended?: boolean;
}

interface PaymentMethodSelectorProps {
  selectedMethod: string;
  onMethodChange: (method: string) => void;
  disabled?: boolean;
  amount?: number; // Required for wallet balance check
}

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  selectedMethod,
  onMethodChange,
  disabled = false,
  amount = 0
}) => {
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [walletLoading, setWalletLoading] = useState(true);

  useEffect(() => {
    loadWalletBalance();
  }, []);

  const loadWalletBalance = async () => {
    if (!amount) return; // Only load balance if amount is provided

    setWalletLoading(true);
    try {
      const balance = await walletService.getBalance();
      setWalletBalance(balance.balance);
    } catch (err) {
      console.error('Failed to load wallet balance:', err);
      setWalletBalance(0); // Default to 0 if failed
    } finally {
      setWalletLoading(false);
    }
  };

  const hasSufficientWalletBalance = walletBalance !== null && walletBalance >= amount;
  const paymentMethods: PaymentMethod[] = [
    {
      id: 'WALLET',
      name: 'Wallet',
      description: walletLoading
        ? 'Loading wallet balance...'
        : walletBalance !== null
        ? `Current balance: RM${walletBalance.toFixed(2)}`
        : 'Pay using your wallet balance',
      icon: <WalletIcon className="w-8 h-8" />,
      processingTime: walletLoading ? 'Checking...' : 'Instant payment',
      recommended: hasSufficientWalletBalance,
    },
    {
      id: 'AIAHAD',
      name: 'AiAhad',
      description: 'Instant online payment via credit card, FPX, and e-wallets',
      icon: <CreditCardIcon className="w-8 h-8" />,
      processingTime: 'Instant approval',
      recommended: !hasSufficientWalletBalance, // Recommend AiAhad if wallet balance is insufficient
    },
    {
      id: 'BANK_TRANSFER',
      name: 'Bank Transfer',
      description: 'Manual bank transfer with receipt upload verification',
      icon: <BanknotesIcon className="w-8 h-8" />,
      processingTime: '2-4 hours verification',
    },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Select Payment Method
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Choose how you'd like to complete your payment. All transactions are secure and encrypted.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {paymentMethods.map((method) => {
          // Check if wallet should be disabled
          const isWalletDisabled = method.id === 'WALLET' && (
            walletLoading ||
            !hasSufficientWalletBalance ||
            disabled
          );

          const isDisabled = disabled || isWalletDisabled;

          return (
            <label
              key={method.id}
              className={`
                relative flex items-start p-4 border-2 rounded-lg transition-all duration-200
                ${selectedMethod === method.id
                  ? 'border-green-500 bg-green-50 ring-2 ring-green-200'
                  : 'border-gray-300 bg-white hover:border-gray-400 hover:shadow-md'
                }
                ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              <input
                type="radio"
                name="paymentMethod"
                value={method.id}
                checked={selectedMethod === method.id}
                onChange={() => !isDisabled && onMethodChange(method.id)}
                disabled={isDisabled}
                className="sr-only"
              />

              <div className="flex items-start space-x-4 flex-1">
                <div className={`
                  flex-shrink-0 rounded-full p-3
                  ${selectedMethod === method.id
                    ? 'bg-green-500 text-white'
                    : method.id === 'WALLET' && !hasSufficientWalletBalance
                    ? 'bg-gray-100 text-gray-400'
                    : 'bg-gray-100 text-gray-600'
                  }
                `}>
                  {method.icon}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <h4 className="text-base font-semibold text-gray-900">
                      {method.name}
                    </h4>
                    {method.recommended && !isDisabled && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Recommended
                      </span>
                    )}
                    {method.id === 'WALLET' && !hasSufficientWalletBalance && !walletLoading && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        Insufficient Balance
                      </span>
                    )}
                  </div>

                  <p className="mt-1 text-sm text-gray-600">
                    {method.description}
                  </p>

                  <div className="mt-2 flex items-center text-sm">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                      method.id === 'WALLET' && !hasSufficientWalletBalance && !walletLoading
                        ? 'bg-gray-100 text-gray-500'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {method.processingTime}
                    </span>
                  </div>
                </div>
              </div>

              {selectedMethod === method.id && !isDisabled && (
                <div className="flex-shrink-0">
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l-1.293-1.293a1 1 0 111.414-1.414L9.414 11.586 14 7a1 1 0 01-1.414 1.414l-8-8a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
              )}
            </label>
          );
        })}
      </div>

      {/* Payment Method Details */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Payment Details:</h4>

        {selectedMethod === 'WALLET' && (
          <div className="space-y-2 text-sm text-gray-600">
            {hasSufficientWalletBalance ? (
              <>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span>Instant payment - no redirects needed</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span>Pay directly from your wallet balance</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span>Automatic booking approval</span>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded">
                <div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <span className="font-medium text-orange-800">Insufficient Balance</span>
                  </div>
                  <p className="text-xs text-orange-700 mt-1">
                    Current balance: RM{walletBalance?.toFixed(2)} | Required: RM{amount.toFixed(2)}
                  </p>
                </div>
                <button
                  onClick={() => window.location.href = '/wallet'}
                  className="px-3 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 transition-colors"
                >
                  Top Up Wallet
                </button>
              </div>
            )}
          </div>
        )}

        {selectedMethod === 'AIAHAD' && (
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>Instant payment confirmation</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>Secure payment processing</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>Automatic booking approval</span>
            </div>
          </div>
        )}

        {selectedMethod === 'BANK_TRANSFER' && (
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span>Upload payment receipt after transfer</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span>Manual verification by admin team</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span>Verification within 2-4 business hours</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentMethodSelector;